import React from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import {
  responsiveBottomDrawerPaperSx,
} from "../../theme/webProfitResponsive";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ContextualHelp({
  title,
  text,
  bullets = [],
  label = "Ayuda",
  dark = false,
  compact = false,
  buttonSx = {},
}) {
  const theme = useTheme();
  const touchLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const dialogId = React.useId();
  const titleId = React.useId();
  const descriptionId = React.useId();
  const open = Boolean(anchorEl);

  const closeHelp = () => setAnchorEl(null);

  const helpContent = (
    <Stack spacing={1.05} sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography
          id={titleId}
          sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.2, pt: 0.25 }}
        >
          {title}
        </Typography>
        <IconButton
          type="button"
          size="small"
          onClick={closeHelp}
          aria-label="Cerrar ayuda"
          sx={{ mt: -0.65, mr: -0.65, flex: "0 0 auto" }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
      {text ? (
        <Typography
          id={descriptionId}
          sx={{ color: "rgba(25,36,47,.66)", fontSize: ".82rem", lineHeight: 1.5 }}
        >
          {text}
        </Typography>
      ) : null}
      {bullets.length ? (
        <Stack component="ul" spacing={0.65} sx={{ m: 0, pl: 2.1 }}>
          {bullets.map((bullet) => (
            <Box component="li" key={bullet} sx={{ color: "rgba(25,36,47,.68)", pl: 0.15 }}>
              <Typography component="span" sx={{ fontSize: ".78rem", lineHeight: 1.45 }}>
                {bullet}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );

  return (
    <>
      <Button
        type="button"
        size="small"
        variant="text"
        startIcon={<HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />}
        aria-label={`${label}: ${title}`}
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? dialogId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minWidth: 0,
          px: compact ? 0.75 : 1,
          py: 0.45,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 900,
          color: dark ? "rgba(255,255,255,.82)" : BRAND.orange,
          bgcolor: dark ? "rgba(255,255,255,.07)" : "rgba(243,90,58,.055)",
          "&:hover": {
            bgcolor: dark ? "rgba(255,255,255,.13)" : "rgba(243,90,58,.11)",
          },
          "& .MuiButton-startIcon": compact
            ? { mr: { xs: 0, sm: 0.5 }, ml: 0 }
            : undefined,
          "&:focus-visible": {
            outline: `2px solid ${dark ? "rgba(255,255,255,.62)" : "rgba(243,90,58,.55)"}`,
            outlineOffset: 2,
          },
          ...buttonSx,
        }}
      >
        <Box component="span" sx={{ display: compact ? { xs: "none", sm: "inline" } : "inline" }}>
          {label}
        </Box>
      </Button>

      {touchLayout ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={closeHelp}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            id: dialogId,
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": titleId,
            "aria-describedby": text ? descriptionId : undefined,
            sx: {
              ...responsiveBottomDrawerPaperSx,
              maxHeight: "min(72dvh, 520px)",
              p: 2,
              pb: "max(16px, env(safe-area-inset-bottom, 0px))",
            },
          }}
        >
          {helpContent}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={closeHelp}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              id: dialogId,
              role: "dialog",
              "aria-labelledby": titleId,
              "aria-describedby": text ? descriptionId : undefined,
              sx: {
                mt: 0.65,
                width: 360,
                maxWidth: "calc(100vw - 24px)",
                p: 1.8,
                borderRadius: 3,
                border: "1px solid rgba(25,36,47,.1)",
                boxShadow: "0 18px 48px rgba(25,36,47,.2)",
              },
            },
          }}
        >
          {helpContent}
        </Popover>
      )}
    </>
  );
}
